/**
 * AudioVisualizerTwoStreams.jsx
 *
 * Two circles for user + assistant with volume-based pulsing.
 * Each circle is inside a container of fixed size (circleSize + pulseMaxSize),
 * so we don't affect overall layout when the ring expands.
 *
 * Outer ring has class "mwai-animation" to be styled or animated by your CSS.
 * If userColor / assistantColor is null, we do NOT inline a color.
 */

const { useState, useRef, useEffect } = wp.element;

/**
 * measureVolume()
 * Reads time-domain data and calculates an RMS volume (~0..~20 typical).
 */
function measureVolume(analyser, dataArray) {
  analyser.getByteTimeDomainData(dataArray);
  let sum = 0;
  for (let i = 0; i < dataArray.length; i++) {
    const val = dataArray[i] - 128;
    sum += val * val;
  }
  return Math.sqrt(sum / dataArray.length); // RMS
}

export default function AudioVisualizerTwoStreams({
  // Streams
  assistantStream = null,
  userStream = null,

  // Colors for the circles
  // If null, we don't apply inline color (you can do it via CSS)
  assistantColor = null,
  userColor = null,

  // UI representations
  // e.g. { emoji: "😊", text: "User", image: "url", use: "image" }
  userUI = { emoji: null, text: null, image: null, use: "text" },
  assistantUI = { emoji: null, text: null, image: null, use: "text" },

  // Attack/Release speeds
  attackSpeed = 0.3,
  releaseSpeed = 0.05,

  // Circle sizing
  circleSize = 50,     // base diameter (inner circle)
  pulseMaxSize = 30,   // how many extra px to grow at max volume
}) {
  // Smoothed volumes (state) for rendering
  const [assistantVolume, setAssistantVolume] = useState(0);
  const [userVolume, setUserVolume] = useState(0);

  // We keep references to the raw volumes for attack/release
  const assistantSmoothedRef = useRef(0);
  const userSmoothedRef = useRef(0);

  // AudioContext + Analyser
  const audioContextRef = useRef(null);
  const assistantAnalyserRef = useRef(null);
  const assistantDataArrayRef = useRef(null);
  const userAnalyserRef = useRef(null);
  const userDataArrayRef = useRef(null);

  useEffect(() => {
    // If no streams, do nothing
    if (!assistantStream && !userStream) {
      return;
    }

    // Create or reuse AudioContext
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    const audioContext = audioContextRef.current;

    let assistantSource;
    let userSource;
    let animationFrameId;

    // Setup assistant audio
    if (assistantStream) {
      assistantSource = audioContext.createMediaStreamSource(assistantStream);
      assistantAnalyserRef.current = audioContext.createAnalyser();
      assistantAnalyserRef.current.fftSize = 1024;
      assistantDataArrayRef.current = new Uint8Array(
        assistantAnalyserRef.current.frequencyBinCount
      );
      assistantSource.connect(assistantAnalyserRef.current);
    }

    // Setup user audio
    if (userStream) {
      userSource = audioContext.createMediaStreamSource(userStream);
      userAnalyserRef.current = audioContext.createAnalyser();
      userAnalyserRef.current.fftSize = 1024;
      userDataArrayRef.current = new Uint8Array(
        userAnalyserRef.current.frequencyBinCount
      );
      userSource.connect(userAnalyserRef.current);
    }

    // Animation loop
    const tick = () => {
      let newAssistantVolume = 0;
      if (assistantAnalyserRef.current && assistantDataArrayRef.current) {
        newAssistantVolume = measureVolume(
          assistantAnalyserRef.current,
          assistantDataArrayRef.current
        );
      }
      let newUserVolume = 0;
      if (userAnalyserRef.current && userDataArrayRef.current) {
        newUserVolume = measureVolume(
          userAnalyserRef.current,
          userDataArrayRef.current
        );
      }

      // Attack/release for assistant
      if (newAssistantVolume > assistantSmoothedRef.current) {
        // Attack
        assistantSmoothedRef.current =
          assistantSmoothedRef.current * (1 - attackSpeed) +
          newAssistantVolume * attackSpeed;
      } else {
        // Release
        assistantSmoothedRef.current =
          assistantSmoothedRef.current * (1 - releaseSpeed) +
          newAssistantVolume * releaseSpeed;
      }

      // Attack/release for user
      if (newUserVolume > userSmoothedRef.current) {
        // Attack
        userSmoothedRef.current =
          userSmoothedRef.current * (1 - attackSpeed) +
          newUserVolume * attackSpeed;
      } else {
        // Release
        userSmoothedRef.current =
          userSmoothedRef.current * (1 - releaseSpeed) +
          newUserVolume * releaseSpeed;
      }

      // Update state
      setAssistantVolume(assistantSmoothedRef.current);
      setUserVolume(userSmoothedRef.current);

      // Next frame
      animationFrameId = requestAnimationFrame(tick);
    };
    tick();

    // Cleanup
    return () => {
      if (assistantSource) assistantSource.disconnect();
      if (assistantAnalyserRef.current) assistantAnalyserRef.current.disconnect();

      if (userSource) userSource.disconnect();
      if (userAnalyserRef.current) userAnalyserRef.current.disconnect();

      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [assistantStream, userStream, attackSpeed, releaseSpeed]);

  // Convert volumes ~0..20 to [0..1]
  const normAssistant = Math.min(assistantVolume / 20, 1);
  const normUser = Math.min(userVolume / 20, 1);

  // Outer ring diameter = base circleSize + extra pulse
  const assistantPulseDiameter = circleSize + normAssistant * pulseMaxSize;
  const userPulseDiameter = circleSize + normUser * pulseMaxSize;

  // The container is always big enough for the max ring
  const containerSize = circleSize + pulseMaxSize; // e.g. 50 + 30 = 80

  // If userColor/assistantColor is null, skip inline styling for backgroundColor
  const userPulseStyle = {
    width: userPulseDiameter,
    height: userPulseDiameter,
    borderRadius: "50%",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    opacity: 0.5,
  };
  if (userColor) {
    userPulseStyle.backgroundColor = userColor;
  }

  const assistantPulseStyle = {
    width: assistantPulseDiameter,
    height: assistantPulseDiameter,
    borderRadius: "50%",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    opacity: 0.5,
  };
  if (assistantColor) {
    assistantPulseStyle.backgroundColor = assistantColor;
  }

  const userCircleStyle = {
    width: circleSize,
    height: circleSize,
    borderRadius: "50%",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    color: "#fff",
  };
  if (userColor) {
    userCircleStyle.backgroundColor = userColor;
  }

  const assistantCircleStyle = {
    width: circleSize,
    height: circleSize,
    borderRadius: "50%",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    color: "#fff",
  };
  if (assistantColor) {
    assistantCircleStyle.backgroundColor = assistantColor;
  }

  // Determine who is "talking"
  let containerClass = "mwai-visualizer";
  if (userVolume > assistantVolume) {
    containerClass += " mwai-user-talking";
  } else if (assistantVolume > userVolume) {
    containerClass += " mwai-assistant-talking";
  }

  // Renders the UI inside the fixed circle
  const renderUI = (uiObj) => {
    if (!uiObj) return null;
    const { emoji, text, image, use } = uiObj;

    switch (use) {
      case "emoji":
        if (emoji) return <span>{emoji}</span>;
        if (text) return <span>{text.slice(0, 1)}</span>;
        return null;
      case "image":
        if (image) {
          return (
            <img
              src={image}
              alt=""
              style={{ width: "100%", height: "100%", borderRadius: "50%" }}
            />
          );
        }
        // fallback to emoji or first-letter
        if (emoji) return <span>{emoji}</span>;
        if (text) return <span>{text.slice(0, 1)}</span>;
        return null;
      case "text":
      default:
        if (text) return <span>{text.slice(0, 1)}</span>;
        if (emoji) return <span>{emoji}</span>;
        return null;
    }
  };

  return (
    <div className={containerClass}>
      {/* User Circle Container (fixed size) */}
      <div
        className="mwai-visualizer-user"
        style={{
          position: "relative",
          width: containerSize,
          height: containerSize,
          overflow: "visible",
        }}
      >
        {/* Outer ring that grows/shrinks with a "mwai-animation" class */}
        <div className="mwai-animation" style={userPulseStyle} />

        {/* Inner fixed circle */}
        <div style={userCircleStyle}>
          {renderUI(userUI)}
        </div>
      </div>

      {/* The line in the middle */}
      <hr className="mwai-visualizer-line" />

      {/* Assistant Circle Container (fixed size) */}
      <div
        className="mwai-visualizer-assistant"
        style={{
          position: "relative",
          width: containerSize,
          height: containerSize,
          overflow: "visible",
        }}
      >
        {/* Outer ring that grows/shrinks with a "mwai-animation" class */}
        <div className="mwai-animation" style={assistantPulseStyle} />

        {/* Inner fixed circle */}
        <div style={assistantCircleStyle}>
          {renderUI(assistantUI)}
        </div>
      </div>
    </div>
  );
}
